// PostHog product-analytics + session-replay wrapper. Lazy-loaded via
// dynamic import so the SDK never lands in the entry chunk.
//
// Consent model (split-gate):
//   - **Event capture** always on. US users + EU users alike. No banner
//     required for events; this is industry-standard for first-party
//     product analytics under CCPA/CPRA opt-out posture.
//   - **Session recording** gated. US users (timezone ≠ Europe/*) get
//     replay on by default. EU users see the cookie banner and replay
//     only starts after Accept.
//   - **Global Privacy Control** browser signal honored — auto-opts the
//     user out of all capture if present.
//
// Sensitive-content masking: chat/prayer/Bible-insight/ministry/learn UI
// renders user-generated content with `className="ph-mask"`. The session
// recorder masks text inside those elements via `maskTextSelector`. The
// composer textarea is also masked via `maskAllInputs: true`. UI chrome
// (buttons, navigation, picker state) records normally so we can replay
// the user journey without leaking the actual content.
//
// Internal accounts opt out automatically on identify so Mason's own
// browsing and staff QA don't pollute the data.

const KEY = import.meta.env.VITE_POSTHOG_KEY
const HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

// Reverse-proxy through our own /ingest/* routes (configured in vercel.json)
// so ad blockers can't filter events. The PostHog SDK reads `api_host` for
// event delivery and `ui_host` for "view in PostHog" deep links — keeping
// `ui_host` pointed at the real host means admin links from the SDK still
// resolve to the dashboard.
const PROXY_HOST = '/ingest'

// CSS selector for elements whose text should be masked in session
// recordings. UI-chrome stays unmasked; only user-generated content gets
// the `ph-mask` class on its render container.
const MASK_TEXT_SELECTOR = '.ph-mask, [data-ph-mask]'

// Internal accounts whose sessions should never count as PostHog data
// (Mason browsing his own site, staff QA on @illuminatefaithai.com).
// On identify we opt-out capturing entirely; on a non-internal user
// signing in to the same device we opt back in so their events count.
const INTERNAL_EMAIL_PATTERNS = [/^mchap2023@gmail\.com$/i, /@illuminatefaithai\.com$/i]

function isInternalEmail(email) {
  if (!email) return false
  return INTERNAL_EMAIL_PATTERNS.some(pattern => pattern.test(email))
}

// Timezone-based EU detection. Cheap, instant, no network call. Misses
// VPN users (acceptable — they're hiding their location anyway) and EU
// expats in the US (extremely rare).
export function isLikelyEUUser() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    return tz.startsWith('Europe/')
  } catch {
    // If the timezone API ever fails, default to treating the user as EU
    // (banner shown, replay gated). Fail-safe toward more privacy, not less.
    return true
  }
}

// Honor the Global Privacy Control browser signal — required in California
// from 2026 onward, voluntary elsewhere. If GPC is on, opt the user out
// regardless of other settings.
function hasGPCSignal() {
  if (typeof navigator === 'undefined') return false
  return navigator.globalPrivacyControl === true
}

// Promise cache so the dynamic import only fires once.
let posthogPromise = null

// Pending state captured before the SDK has loaded.
let pendingUserId = null
let pendingEmail = null
let pendingReset = false
let pendingCaptures = []

// Tracks whether session recording is currently authorized. Flipped by
// `enableSessionRecording()` once the user passes the consent gate (or
// immediately for non-EU users via `bootPostHog`).
let recordingAllowed = false

function loadPostHog() {
  if (!KEY) return null
  if (posthogPromise) return posthogPromise
  posthogPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      try {
        posthog.init(KEY, {
          api_host: PROXY_HOST,
          ui_host: HOST,
          // Anonymous events get counted as their own person profile.
          // Prior `identified_only` mode dropped pre-signup events from
          // the persons view, undercounting funnel entries.
          person_profiles: 'always',
          capture_pageview: true,
          capture_pageleave: true,
          autocapture: true,
          // Respect Do Not Track + GPC from the SDK side too.
          respect_dnt: true,
          // Replay starts disabled. `enableSessionRecording()` flips it
          // on for US users immediately or for EU users post-consent.
          disable_session_recording: true,
          session_recording: {
            maskAllInputs: true,
            maskTextSelector: MASK_TEXT_SELECTOR,
            // Mask password fields explicitly even though maskAllInputs
            // already covers them — belt and suspenders.
            maskInputOptions: { password: true },
          },
          mask_all_text: false,
          loaded: ph => {
            if (hasGPCSignal()) {
              ph.opt_out_capturing()
            }
            if (pendingReset) {
              ph.reset()
              pendingReset = false
            } else if (pendingUserId) {
              if (isInternalEmail(pendingEmail)) {
                ph.opt_out_capturing()
              } else if (!hasGPCSignal()) {
                ph.opt_in_capturing()
              }
              ph.identify(pendingUserId, pendingEmail ? { email: pendingEmail } : undefined)
              pendingUserId = null
              pendingEmail = null
            }
            if (recordingAllowed && !hasGPCSignal()) {
              try {
                ph.startSessionRecording?.()
              } catch {
                // ignore recorder failures
              }
            }
            for (const [event, props] of pendingCaptures) {
              try {
                ph.capture(event, props)
              } catch {
                // swallow individual capture failures
              }
            }
            pendingCaptures = []
          },
        })
        return posthog
      } catch (err) {
        console.warn('[posthog] init failed:', err?.message || err)
        return null
      }
    })
    .catch(err => {
      console.warn('[posthog] dynamic import failed:', err?.message || err)
      return null
    })
  return posthogPromise
}

// Boot PostHog with event capture on. Called from CookieConsentContext
// on every page load — no consent required for basic event tracking.
export function bootPostHog() {
  if (!KEY) {
    if (import.meta.env.DEV) {
      console.info('[posthog] disabled — set VITE_POSTHOG_KEY to enable analytics')
    }
    return
  }
  loadPostHog()
}

// Authorize session recording. Called immediately for non-EU users and
// post-consent for EU users. Idempotent.
export function enableSessionRecording() {
  recordingAllowed = true
  const promise = posthogPromise
  if (!promise) return
  promise
    .then(posthog => {
      if (!posthog) return
      if (hasGPCSignal()) return
      try {
        posthog.startSessionRecording?.()
      } catch {
        // ignore
      }
    })
    .catch(() => {})
}

// Revoke session recording — called when an EU user clicks Decline.
export function disableSessionRecording() {
  recordingAllowed = false
  const promise = posthogPromise
  if (!promise) return
  promise
    .then(posthog => {
      if (!posthog) return
      try {
        posthog.stopSessionRecording?.()
      } catch {
        // ignore
      }
    })
    .catch(() => {})
}

export function identifyUser(userId, email) {
  if (!userId) return
  pendingUserId = userId
  pendingEmail = email
  pendingReset = false
  const promise = posthogPromise
  if (!promise) return
  promise
    .then(posthog => {
      if (posthog && pendingUserId === userId) {
        if (isInternalEmail(email)) {
          posthog.opt_out_capturing()
        } else if (!hasGPCSignal()) {
          posthog.opt_in_capturing()
        }
        posthog.identify(userId, email ? { email } : undefined)
        pendingUserId = null
        pendingEmail = null
      }
    })
    .catch(() => {})
}

export function resetUser() {
  pendingReset = true
  pendingUserId = null
  const promise = posthogPromise
  if (!promise) return
  promise
    .then(posthog => {
      if (posthog && pendingReset) {
        posthog.reset()
        pendingReset = false
      }
    })
    .catch(() => {})
}

export function capture(event, props) {
  if (!event) return
  if (!posthogPromise) {
    pendingCaptures.push([event, props])
    return
  }
  posthogPromise
    .then(posthog => {
      if (!posthog) return
      try {
        posthog.capture(event, props)
      } catch {
        // never let analytics failures break the app
      }
    })
    .catch(() => {})
}

// Sets a property on the person profile that only writes the first time —
// used for first-ever-action milestones like "first_chat_at" so the value
// reflects the user's actual first action, not their most recent one.
export function setPersonPropertyOnce(props) {
  if (!props || !posthogPromise) return
  posthogPromise
    .then(posthog => {
      if (!posthog) return
      try {
        posthog.setPersonPropertiesForFlags?.(props)
        // setPersonProperties with $set_once writes immutable first-touch values
        posthog.capture('$set_once', { $set_once: props })
      } catch {
        // ignore
      }
    })
    .catch(() => {})
}
